import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminNav from '@/components/AdminNav';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { Users, Plus, Search, Edit, Bell, MessageSquare, Download, AlertTriangle, CloudUpload, Settings, CreditCard, FolderOpen, FileText, ArrowLeft } from 'lucide-react';

const AdminUsers = ({ user, setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const divisionIdFilter = searchParams.get('divisionId');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [crafts, setCrafts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [selectedRequiredCourses, setSelectedRequiredCourses] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [billingInfo, setBillingInfo] = useState(null);
  const [filterDivisionName, setFilterDivisionName] = useState(null);
  
  // Helper function to get drug screen status
  const getDrugScreenStatus = (expirationDate) => {
    if (!expirationDate) return { status: 'none', label: 'Not Set', color: 'gray' };
    
    const expDate = new Date(expirationDate);
    const now = new Date();
    const daysUntilExpiration = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiration < 0) {
      return { status: 'expired', label: 'Expired', color: 'red', days: Math.abs(daysUntilExpiration) };
    } else if (daysUntilExpiration <= 30) {
      return { status: 'expiring', label: `${daysUntilExpiration}d left`, color: 'yellow', days: daysUntilExpiration };
    } else {
      return { status: 'valid', label: 'Valid', color: 'green', days: daysUntilExpiration };
    }
  };
  
  // Create User States
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [createUserForm, setCreateUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'worker',
    phone: '',
    craft: '',
    divisionId: ''
  });
  
  // Edit User States
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: '',
    isActive: true,
    drugScreenExpirationDate: ''
  });
  
  // Transfer Worker States
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferWorker, setTransferWorker] = useState(null);
  const [destinationCompanyCode, setDestinationCompanyCode] = useState('');
  
  // Division Transfer States
  const [showDivisionTransferDialog, setShowDivisionTransferDialog] = useState(false);
  const [divisionTransferWorker, setDivisionTransferWorker] = useState(null);
  const [targetDivisionId, setTargetDivisionId] = useState('');
  
  // Cloud Storage States
  const [cloudStorageConfig, setCloudStorageConfig] = useState(null);
  const [showCloudStorageDialog, setShowCloudStorageDialog] = useState(false);
  const [cloudStorageForm, setCloudStorageForm] = useState({
    provider: 'aws_s3',
    accessKey: '',
    secretKey: '',
    bucketName: '',
    region: '',
    customEndpoint: ''
  });
  const [savingCloudStorage, setSavingCloudStorage] = useState(false);
  
  // Message States
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [messageForm, setMessageForm] = useState({
    recipients: 'all',
    subject: '',
    message: ''
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Reminder States
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    recipients: 'all',
    message: '',
    selectedCourses: []
  });
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    loadData();
    loadBillingInfo();
    loadCloudStorageConfig();
  }, []);

  const loadData = async () => {
    try {
      // Build users URL with optional division filter
      const usersUrl = divisionIdFilter ? `/admin/users?divisionId=${divisionIdFilter}` : '/admin/users';
      
      const [analyticsRes, usersRes, craftsRes, allCoursesRes, divisionsRes] = await Promise.all([
        axiosInstance.get('/admin/analytics'),
        axiosInstance.get(usersUrl),
        axiosInstance.get('/crafts'),
        axiosInstance.get('/admin/all-courses'),
        axiosInstance.get('/divisions')
      ]);
      
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data);
      setCrafts(craftsRes.data);
      setAllCourses(allCoursesRes.data);
      setDivisions(divisionsRes.data);
      
      // Set filter division name for display (only if division actually exists)
      if (divisionIdFilter) {
        const division = divisionsRes.data.find(d => d.id === divisionIdFilter);
        if (division) {
          setFilterDivisionName(division.name);
        } else {
          // Division not found - clear the filter to avoid showing "Unknown"
          setFilterDivisionName(null);
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(error.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadBillingInfo = async () => {
    try {
      const response = await axiosInstance.get('/billing/pricing');
      setBillingInfo(response.data);
    } catch (error) {
      console.error('Failed to load billing info:', error);
    }
  };

  const loadCloudStorageConfig = async () => {
    try {
      const response = await axiosInstance.get('/admin/cloud-storage');
      setCloudStorageConfig(response.data);
    } catch (error) {
      console.error('Failed to load cloud storage config:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (createUserForm.role === 'worker' && !createUserForm.craft) {
      toast.error(t('adminToasts.PleaseSelectACraftForTheWorker'));
      return;
    }
    
    if (createUserForm.role === 'division_admin' && !createUserForm.divisionId) {
      toast.error(t('adminToasts.DivisionAdminMustBeAssignedToADivision'));
      return;
    }
    
    try {
      const response = await axiosInstance.post('/admin/users', createUserForm);
      toast.success(response.data.message || 'User created successfully!');
      
      setCreateUserForm({
        name: '',
        email: '',
        password: '',
        role: 'worker',
        phone: '',
        craft: '',
        divisionId: ''
      });
      setShowCreateUserDialog(false);
      loadData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const openEditUserDialog = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditUserForm({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      password: '',
      phone: userToEdit.phone || '',
      role: userToEdit.role || '',
      isActive: userToEdit.isActive !== undefined ? userToEdit.isActive : true,
      drugScreenExpirationDate: userToEdit.drugScreenExpirationDate || ''
    });
    setShowEditUserDialog(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = {};
      if (editUserForm.name) updateData.name = editUserForm.name;
      if (editUserForm.email) updateData.email = editUserForm.email;
      if (editUserForm.password) updateData.password = editUserForm.password;
      if (editUserForm.phone) updateData.phone = editUserForm.phone;
      if (editUserForm.role) updateData.role = editUserForm.role;
      updateData.isActive = editUserForm.isActive;
      
      await axiosInstance.patch(`/admin/users/${editingUser.id}`, updateData);
      
      toast.success(t('adminToasts.UserUpdatedSuccessfully'));
      setShowEditUserDialog(false);
      setEditingUser(null);
      loadData();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleToggleWorkerStatus = async (worker) => {
    const action = worker.isActive ? 'deactivate' : 'activate';
    const confirmMsg = worker.isActive 
      ? `Deactivate ${worker.name}? They will not be able to login and won't count toward billing.`
      : `Reactivate ${worker.name}? They will be able to login and will count toward billing.`;
    
    if (!confirm(confirmMsg)) return;

    try {
      const response = await axiosInstance.post('/companies/toggle-worker-status', {
        workerId: worker.id
      });

      toast.success(response.data.message);
      loadData();
      loadBillingInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} worker`);
    }
  };

  const handleTransferWorker = async () => {
    if (!destinationCompanyCode.trim()) {
      toast.error(t('adminToasts.PleaseEnterDestinationCompanyCode'));
      return;
    }

    try {
      const response = await axiosInstance.post('/companies/transfer-worker', {
        workerId: transferWorker.id,
        destinationCompanyCode: destinationCompanyCode.toUpperCase()
      });

      toast.success(`${transferWorker.name} transferred to ${response.data.to.companyName}`);
      setShowTransferDialog(false);
      setTransferWorker(null);
      setDestinationCompanyCode('');
      loadData();
      loadBillingInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    }
  };

  const handleDivisionTransfer = async () => {
    if (!targetDivisionId) {
      toast.error(t('adminToasts.PleaseSelectATargetDivision'));
      return;
    }

    try {
      // Handle "remove from division" case
      const finalDivisionId = targetDivisionId === '__remove__' ? '' : targetDivisionId;
      
      const response = await axiosInstance.post('/divisions/transfer-worker', {
        userId: divisionTransferWorker.id,
        toDivisionId: finalDivisionId
      });

      const message = finalDivisionId 
        ? `${divisionTransferWorker.name} transferred to ${response.data.newDivisionName}`
        : `${divisionTransferWorker.name} removed from division`;
      
      toast.success(message);
      setShowDivisionTransferDialog(false);
      setDivisionTransferWorker(null);
      setTargetDivisionId('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Division transfer failed');
    }
  };

  const sendMessageToWorkers = async () => {
    try {
      let recipientIds = [];
      
      if (messageForm.recipients === 'all') {
        recipientIds = users.filter(u => u.role === 'worker' || u.role === 'learner').map(u => u.id);
      } else if (messageForm.recipients === 'incomplete') {
        const allUsers = users.filter(u => u.role === 'worker' || u.role === 'learner');
        recipientIds = allUsers.map(u => u.id);
      } else if (messageForm.recipients === 'specific') {
        recipientIds = selectedUsers;
      }
      
      if (recipientIds.length === 0) {
        toast.error(t('adminToasts.NoRecipientsSelected'));
        return;
      }
      
      await axiosInstance.post('/messages/send', {
        toUserIds: recipientIds,
        subject: messageForm.subject,
        message: messageForm.message,
        type: 'reminder'
      });
      
      toast.success(`Message sent to ${recipientIds.length} worker(s)!`);
      setShowMessageDialog(false);
      setMessageForm({ subject: '', message: '', recipients: 'all' });
      setSelectedUsers([]);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const sendTrainingReminders = async () => {
    try {
      setSendingReminders(true);
      
      const reminderData = {
        recipientType: reminderForm.recipientType,
        messageType: reminderForm.messageType,
        customMessage: reminderForm.customMessage,
        userIds: reminderForm.userIds
      };
      
      const response = await axiosInstance.post('/admin/send-training-reminders', reminderData);
      
      toast.success(`Training reminders sent to ${response.data.sent} worker(s)!`);
      
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} email(s) failed to send`);
      }
      
      setShowReminderDialog(false);
      setReminderForm({
        recipientType: 'all',
        messageType: 'general',
        customMessage: '',
        userIds: []
      });
    } catch (error) {
      console.error('Failed to send training reminders:', error);
      toast.error(error.response?.data?.detail || 'Failed to send training reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const saveCloudStorageConfig = async () => {
    if (!cloudStorageForm.provider || !cloudStorageForm.accessKey || !cloudStorageForm.secretKey || !cloudStorageForm.bucketName) {
      toast.error(t('adminToasts.PleaseFillInAllRequiredFields'));
      return;
    }

    try {
      setSavingCloudStorage(true);
      await axiosInstance.post('/admin/cloud-storage', cloudStorageForm);
      toast.success(t('adminToasts.CloudStorageConfigurationSavedSuccessfully'));
      setShowCloudStorageDialog(false);
      loadCloudStorageConfig();
    } catch (error) {
      console.error('Failed to save cloud storage config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSavingCloudStorage(false);
    }
  };

  const deleteCloudStorageConfig = async () => {
    if (!window.confirm('Are you sure you want to delete your cloud storage configuration? Uploaded files will no longer be accessible.')) {
      return;
    }

    try {
      await axiosInstance.delete('/admin/cloud-storage');
      toast.success(t('adminToasts.CloudStorageConfigurationDeleted'));
      setCloudStorageConfig(null);
    } catch (error) {
      console.error('Failed to delete cloud storage config:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete configuration');
    }
  };

  const downloadCertificates = async () => {
    try {
      const response = await axiosInstance.get('/admin/download-certificates');
      const certificates = response.data;
      
      if (certificates.length === 0) {
        toast.info(t('adminToasts.NoCertificatesAvailableToDownload'));
        return;
      }
      
      const headers = ['Employee Name', 'Email', 'Course', 'Issued Date', 'Certificate ID'];
      const csvContent = [
        headers.join(','),
        ...certificates.map(cert => [
          cert.userName,
          cert.userEmail,
          cert.courseTitle,
          new Date(cert.issuedAt).toLocaleDateString(),
          cert.id
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificates_${analytics?.company?.name || 'company'}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${certificates.length} certificates`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('adminToasts.FailedToDownloadCertificates'));
    }
  };

  const filteredUsers = (users || []).filter(u => {
    if (!userSearchQuery.trim()) return true;
    
    const query = userSearchQuery.toLowerCase();
    return (
      u.name.toLowerCase().includes(query) ||
      u.email.toLowerCase().includes(query) ||
      u.role.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNav user={user} onLogout={handleLogout} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('adminUI.LoadingUserManagement')}</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav user={user} onLogout={handleLogout} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* SafetyMind AI Branding */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-lg flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-lg">SM</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{t('adminUI.SafetymindAi')}</h2>
              <p className="text-xs text-gray-500 italic">{t('adminUI.SafetyThatThinks')}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {filterDivisionName ? `${filterDivisionName} - Users` : 'Settings & User Management'}
          </h1>
          <p className="text-gray-600 mt-1">
            {filterDivisionName 
              ? `Showing users from ${filterDivisionName} division` 
              : 'Central hub for users, company settings, and file management'}
          </p>
          {filterDivisionName && (
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={() => navigate('/admin/users')}
            >
              Clear Filter - Show All Users
            </Button>
          )}
        </div>

        {/* User Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">{t('adminUI.TotalUsers')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{users.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">{t('adminUI.ActiveUsers')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {users.filter(u => u.isActive).length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">{t('adminUI.Workers')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {users.filter(u => u.role === 'worker').length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">{t('adminUI.Supervisors')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {users.filter(u => u.role === 'supervisor').length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Billing Status Banner */}
        <div id="billing-section">
        {user.role === 'company_admin' && billingInfo && billingInfo.subscriptionStatus === 'trial' && (
          <Card className="mb-6 border-yellow-300 bg-yellow-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{t('adminUI.FreeTrialActive')}</h3>
                    <p className="text-sm text-slate-600">
                      Your 7-day trial ends on {new Date(billingInfo.trialEndDate).toLocaleDateString()}. 
                      Subscribe to continue accessing SafetyMind AI.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/admin/old-dashboard')}
                  className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
                >
                  View Pricing & Subscribe
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Subscription Billing */}
        {user.role === 'company_admin' && billingInfo && billingInfo.subscriptionStatus === 'active' && (
          <Card className="mb-6 border-green-300 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{t('adminUI.ActiveSubscription')}</h3>
                    <p className="text-sm text-slate-600">
                      {billingInfo.employee_count} employee licenses • ${billingInfo.price_per_learner}/year each
                      {billingInfo.nextBillingDate && (
                        <span> • Next billing: {new Date(billingInfo.nextBillingDate).toLocaleDateString()}</span>
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/admin/old-dashboard')}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                >
                  Manage Licenses
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        </div>

        {/* Company Settings */}
        <div id="company-settings-section">
        {user.role === 'company_admin' && analytics?.company && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Company Settings - {analytics.company.name}</CardTitle>
              <CardDescription>{t('adminUI.ConfigureCompanyPreferencesAndRequirements')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Required Courses */}
              <div>
                <Label className="text-base font-semibold">{t('adminUI.RequiredCourses')}</Label>
                <p className="text-sm text-slate-600 mb-4">{t('adminUI.SelectCoursesThatAllEmployeesMustComplete')}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto p-4 bg-slate-50 rounded-lg">
                  {allCourses.map(course => {
                    const currentSelection = selectedRequiredCourses !== null ? selectedRequiredCourses : (analytics.company.requiredCourses || []);
                    const isRequired = currentSelection.includes(course.id);
                    return (
                      <div
                        key={course.id}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          isRequired 
                            ? 'border-orange-500 bg-orange-50' 
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                        onClick={() => {
                          const newRequired = isRequired
                            ? currentSelection.filter(id => id !== course.id)
                            : [...currentSelection, course.id];
                          setSelectedRequiredCourses(newRequired);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm">{course.title}</h4>
                            <p className="text-xs text-slate-600 mt-1">{course.category} • {course.duration} min</p>
                          </div>
                          {isRequired && (
                            <Badge className="bg-orange-500 text-white">{t('adminUI.Required')}</Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="text-sm text-slate-600">
                    {(selectedRequiredCourses !== null ? selectedRequiredCourses : (analytics.company.requiredCourses || [])).length} of {allCourses.length} courses selected
                  </p>
                  <div className="flex gap-2">
                    {(selectedRequiredCourses !== null ? selectedRequiredCourses : (analytics.company.requiredCourses || [])).length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedRequiredCourses([])}
                      >
                        Clear All
                      </Button>
                    )}
                    <Button
                      className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
                      size="sm"
                      onClick={async () => {
                        try {
                          const coursesToSave = selectedRequiredCourses !== null 
                            ? selectedRequiredCourses 
                            : analytics.company.requiredCourses || [];
                          
                          await axiosInstance.put('/companies/required-courses', {
                            courseIds: coursesToSave
                          });
                          
                          setSelectedRequiredCourses(null);
                          await loadData();
                          toast.success(`Required courses updated! ${coursesToSave.length} courses selected.`);
                        } catch (error) {
                          console.error('Failed to save required courses:', error);
                          toast.error(error.response?.data?.detail || 'Failed to update required courses');
                        }
                      }}
                      disabled={selectedRequiredCourses === null}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        </div>

        {/* Cloud Storage Configuration */}
        <div id="cloud-storage-section">
        {user.role === 'company_admin' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t('adminUI.CloudStorageConfiguration')}</CardTitle>
              <CardDescription>{t('adminUI.ConfigureYourOwnCloudStorageForPersistentFileUploads')}</CardDescription>
            </CardHeader>
            <CardContent>
              {cloudStorageConfig ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-green-900">✅ Cloud Storage Configured</h4>
                        <p className="text-sm text-green-700 mt-1">
                          Provider: <strong>{cloudStorageConfig.provider.replace('_', ' ').toUpperCase()}</strong>
                        </p>
                        <p className="text-sm text-green-700">
                          Bucket: <strong>{cloudStorageConfig.bucketName}</strong>
                        </p>
                        {cloudStorageConfig.region && (
                          <p className="text-sm text-green-700">
                            Region: <strong>{cloudStorageConfig.region}</strong>
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCloudStorageForm({
                              provider: cloudStorageConfig.provider,
                              accessKey: cloudStorageConfig.accessKey,
                              secretKey: '',
                              bucketName: cloudStorageConfig.bucketName,
                              region: cloudStorageConfig.region || '',
                              customEndpoint: cloudStorageConfig.customEndpoint || ''
                            });
                            setShowCloudStorageDialog(true);
                          }}
                        >
                          Update
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={deleteCloudStorageConfig}
                          className="text-red-600 hover:text-red-700"
                        >{t('adminUI.Delete')}</Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-semibold text-yellow-900">⚠️ No Cloud Storage Configured</h4>
                    <p className="text-sm text-yellow-700 mt-1">{t('adminUI.FilesAreCurrentlyStoredLocallyAndWillBeLostOnDeployment')}</p>
                  </div>
                  <Button onClick={() => setShowCloudStorageDialog(true)}>
                    <CloudUpload className="w-4 h-4 mr-2" />
                    Configure Cloud Storage
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        </div>

        {/* Users Table */}
        <div id="users-section">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t('adminUI.AllUsers')}</CardTitle>
                <CardDescription>{t('adminUI.ManagePlatformUsers')}</CardDescription>
              </div>
              <div className="flex gap-3 items-center">
                <Button
                  onClick={() => setShowCreateUserDialog(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create User
                </Button>
                <Button
                  onClick={downloadCertificates}
                  variant="outline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Certificates
                </Button>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="w-full pl-9"
                  />
                  {userSearchQuery && (
                    <button
                      onClick={() => setUserSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredUsers.length === 0 && userSearchQuery ? (
              <div className="text-center py-8 text-slate-500">
                <p>No users found matching "{userSearchQuery}"</p>
                <Button
                  variant="link"
                  onClick={() => setUserSearchQuery('')}
                  className="mt-2"
                >
                  Clear search
                </Button>
              </div>
            ) : (
              <div>
                <div className="text-sm text-slate-600 mb-4">
                  Showing {filteredUsers.length} of {users.length} users
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>{t('adminUI.Email')}</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>{t('adminUI.Division')}</TableHead>
                      <TableHead>{t('adminUI.DrugScreen')}</TableHead>
                      <TableHead>{t('adminUI.Company')}</TableHead>
                      {user.role === 'company_admin' && <TableHead>{t('adminUI.Actions')}</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map(u => (
                      <TableRow 
                        key={u.id}
                        className={!u.isActive ? 'opacity-50 bg-slate-50' : ''}
                      >
                        <TableCell className="font-medium">
                          {u.name}
                          {!u.isActive && (
                            <Badge variant="outline" className="ml-2 text-xs border-red-200 text-red-600">{t('adminUI.Inactive')}</Badge>
                          )}
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Badge
                            className={
                              u.role === 'company_admin'
                                ? 'bg-purple-100 text-purple-800'
                                : u.role === 'supervisor'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }
                          >
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {u.divisionName ? (
                            <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">
                              {u.divisionName}
                            </Badge>
                          ) : (
                            <span className="text-gray-400 text-sm">{t('adminUI.NoDivision')}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const status = getDrugScreenStatus(u.drugScreenExpirationDate);
                            return (
                              <Badge 
                                variant="outline"
                                className={
                                  status.color === 'red' ? 'bg-red-50 text-red-700 border-red-200' :
                                  status.color === 'yellow' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                  status.color === 'green' ? 'bg-green-50 text-green-700 border-green-200' :
                                  'bg-gray-50 text-gray-500 border-gray-200'
                                }
                              >
                                {status.label}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell>{analytics.company?.name || 'N/A'}</TableCell>
                        {user.role === 'company_admin' && (
                          <TableCell>
                            {u.role === 'worker' || u.role === 'supervisor' ? (
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditUserDialog(u)}
                                >
                                  Edit
                                </Button>
                                {divisions.length > 0 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setDivisionTransferWorker(u);
                                      setTargetDivisionId('');
                                      setShowDivisionTransferDialog(true);
                                    }}
                                    disabled={!u.isActive}
                                    className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                                  >
                                    Transfer Division
                                  </Button>
                                )}
                                {user?.role === 'super_admin' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setTransferWorker(u);
                                      setShowTransferDialog(true);
                                    }}
                                    disabled={!u.isActive}
                                  >
                                    Transfer
                                  </Button>
                                )}
                                <Button
                                  variant={u.isActive ? "destructive" : "default"}
                                  size="sm"
                                  onClick={() => handleToggleWorkerStatus(u)}
                                >
                                  {u.isActive ? 'Deactivate' : 'Reactivate'}
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">N/A</span>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
        </div>

        {/* Create User Dialog */}
        <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.CreateNewUser')}</DialogTitle>
              <DialogDescription>{t('adminUI.AddANewUserToYourCompany')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label>{t('adminUI.Name')}</Label>
                <Input
                  value={createUserForm.name}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t('adminUI.Email')}</Label>
                <Input
                  type="email"
                  value={createUserForm.email}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t('adminUI.Password')}</Label>
                <Input
                  type="password"
                  value={createUserForm.password}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t('adminUI.Role')}</Label>
                <Select
                  value={createUserForm.role}
                  onValueChange={(value) => setCreateUserForm({ ...createUserForm, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="worker">Worker</SelectItem>
                    <SelectItem value="service_worker">Service Truck</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="superintendent">Superintendent</SelectItem>
                    <SelectItem value="division_admin">Division Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('adminUI.Phone')}</Label>
                <Input
                  value={createUserForm.phone}
                  onChange={(e) => setCreateUserForm({ ...createUserForm, phone: e.target.value })}
                />
              </div>
              {createUserForm.role === 'worker' && (
                <div>
                  <Label>{t('adminUI.Craft')}</Label>
                  <Select
                    value={createUserForm.craft}
                    onValueChange={(value) => setCreateUserForm({ ...createUserForm, craft: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select craft" />
                    </SelectTrigger>
                    <SelectContent>
                      {crafts.map(craft => (
                        <SelectItem key={craft.id} value={craft.id}>{craft.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Division Assignment */}
              {divisions.length > 0 && (
                <div>
                  <Label>
                    Division {createUserForm.role === 'division_admin' && <span className="text-red-500">*</span>}
                    {createUserForm.role !== 'division_admin' && <span className="text-gray-400">(Optional)</span>}
                  </Label>
                  <Select
                    value={createUserForm.divisionId || '__none__'}
                    onValueChange={(value) => setCreateUserForm({ ...createUserForm, divisionId: value === '__none__' ? '' : value })}
                    required={createUserForm.role === 'division_admin'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={createUserForm.role === 'division_admin' ? "Select division (required)" : "No division (assign later)"} />
                    </SelectTrigger>
                    <SelectContent>
                      {createUserForm.role !== 'division_admin' && <SelectItem value="__none__">No Division</SelectItem>}
                      {divisions.map(division => (
                        <SelectItem key={division.id} value={division.id}>{division.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {createUserForm.role === 'division_admin' 
                      ? 'Division Admin manages all aspects of their assigned division'
                      : 'Users can be assigned to divisions for separate safety programs'
                    }
                  </p>
                </div>
              )}
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateUserDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">{t('adminUI.CreateUser')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.EditUser')}</DialogTitle>
              <DialogDescription>{t('adminUI.UpdateUserInformation')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={editUserForm.name}
                  onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                />
              </div>
              <div>
                <Label>{t('adminUI.Email')}</Label>
                <Input
                  type="email"
                  value={editUserForm.email}
                  onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                />
              </div>
              <div>
                <Label>New Password (leave blank to keep current)</Label>
                <Input
                  type="password"
                  value={editUserForm.password}
                  onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })}
                />
              </div>
              <div>
                <Label>{t('adminUI.Phone')}</Label>
                <Input
                  value={editUserForm.phone}
                  onChange={(e) => setEditUserForm({ ...editUserForm, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>{t('adminUI.DrugScreenExpirationDate')}</Label>
                <Input
                  type="date"
                  value={editUserForm.drugScreenExpirationDate ? editUserForm.drugScreenExpirationDate.split('T')[0] : ''}
                  onChange={(e) => setEditUserForm({ ...editUserForm, drugScreenExpirationDate: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                />
                <p className="text-xs text-slate-500 mt-1">{t('adminUI.TrackWorkerDrugScreeningCertificationExpiration')}</p>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={editUserForm.role} onValueChange={(v) => setEditUserForm({ ...editUserForm, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="worker">Worker</SelectItem>
                    <SelectItem value="service_worker">Service Truck</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="superintendent">Superintendent</SelectItem>
                    <SelectItem value="division_admin">Division Admin</SelectItem>
                    <SelectItem value="company_admin">Company Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={editUserForm.isActive}
                  onChange={(e) => setEditUserForm({ ...editUserForm, isActive: e.target.checked })}
                />
                <Label htmlFor="isActive">{t('adminUI.Active')}</Label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditUserDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">{t('adminUI.UpdateUser')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Transfer Worker Dialog */}
        <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.TransferWorker')}</DialogTitle>
              <DialogDescription>
                Transfer {transferWorker?.name} to another company
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('adminUI.DestinationCompanyCode')}</Label>
                <Input
                  placeholder="Enter company code"
                  value={destinationCompanyCode}
                  onChange={(e) => setDestinationCompanyCode(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTransferDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleTransferWorker}>{t('adminUI.TransferWorker')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Division Transfer Dialog */}
        <Dialog open={showDivisionTransferDialog} onOpenChange={setShowDivisionTransferDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('adminUI.TransferToDifferentDivision')}</DialogTitle>
              <DialogDescription>
                Move {divisionTransferWorker?.name} to another division within the company
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Current Division:</strong> {divisionTransferWorker?.divisionName || 'No division assigned'}
                </p>
              </div>
              
              <div>
                <Label>{t('adminUI.TargetDivision')}</Label>
                <Select value={targetDivisionId} onValueChange={setTargetDivisionId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select target division" />
                  </SelectTrigger>
                  <SelectContent>
                    {divisions.filter(d => d.id !== divisionTransferWorker?.divisionId).map(division => (
                      <SelectItem key={division.id} value={division.id}>
                        {division.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="__remove__">Remove from division</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">{t('adminUI.WorkerWillLoseAccessToCurrentDivisionDataAndGainAccessToNewD')}</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDivisionTransferDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleDivisionTransfer} className="bg-cyan-600 hover:bg-cyan-700">{t('adminUI.TransferToDivision')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cloud Storage Configuration Dialog */}
        <Dialog open={showCloudStorageDialog} onOpenChange={setShowCloudStorageDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.ConfigureCloudStorage')}</DialogTitle>
              <DialogDescription>{t('adminUI.SendTrainingRemindersOrImportantNotifications')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>{t('adminUI.Recipients')}</Label>
                <Select
                  value={messageForm.recipients}
                  onValueChange={(value) => setMessageForm({ ...messageForm, recipients: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Workers/Learners</SelectItem>
                    <SelectItem value="incomplete">Workers with Incomplete Training</SelectItem>
                    <SelectItem value="specific">Specific Users</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {messageForm.recipients === 'specific' && (
                <div>
                  <Label>{t('adminUI.SelectUsers')}</Label>
                  <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-2">
                    {users.filter(u => u.role === 'worker' || u.role === 'learner').map(u => (
                      <div key={u.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(u.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers([...selectedUsers, u.id]);
                            } else {
                              setSelectedUsers(selectedUsers.filter(id => id !== u.id));
                            }
                          }}
                        />
                        <span className="text-sm">{u.name} ({u.email})</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{selectedUsers.length} user(s) selected</p>
                </div>
              )}

              <div>
                <Label>{t('adminUI.Subject')}</Label>
                <Input
                  placeholder="Message subject"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                />
              </div>
              <div>
                <Label>{t('adminUI.Message')}</Label>
                <Textarea
                  placeholder="Your message..."
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  rows={5}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMessageDialog(false)}>
                Cancel
              </Button>
              <Button onClick={sendMessageToWorkers}>{t('adminUI.SendMessage')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Training Reminder Dialog */}
        <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.SendTrainingReminder')}</DialogTitle>
              <DialogDescription>{t('adminUI.SendAutomatedTrainingRemindersToEmployees')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>{t('adminUI.RecipientType')}</Label>
                <Select
                  value={reminderForm.recipientType}
                  onValueChange={(value) => setReminderForm({ ...reminderForm, recipientType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Workers</SelectItem>
                    <SelectItem value="not_started">Workers Who Haven't Started Training</SelectItem>
                    <SelectItem value="incomplete">Workers with Incomplete Training</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('adminUI.MessageType')}</Label>
                <Select
                  value={reminderForm.messageType}
                  onValueChange={(value) => setReminderForm({ ...reminderForm, messageType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Training Reminder</SelectItem>
                    <SelectItem value="not_started">Not Started Reminder</SelectItem>
                    <SelectItem value="incomplete">Incomplete Training Reminder</SelectItem>
                    <SelectItem value="custom">Custom Message</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {reminderForm.messageType === 'custom' && (
                <div>
                  <Label>{t('adminUI.CustomMessage')}</Label>
                  <Textarea
                    placeholder="Your custom reminder message..."
                    value={reminderForm.customMessage}
                    onChange={(e) => setReminderForm({ ...reminderForm, customMessage: e.target.value })}
                    rows={5}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReminderDialog(false)}>
                Cancel
              </Button>
              <Button onClick={sendTrainingReminders} disabled={sendingReminders}>
                {sendingReminders ? 'Sending...' : 'Send Reminders'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cloud Storage Configuration Dialog */}
        <Dialog open={showCloudStorageDialog} onOpenChange={setShowCloudStorageDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.ConfigureCloudStorage')}</DialogTitle>
              <DialogDescription>{t('adminUI.SetUpYourCloudStorageProviderForPersistentFileStorage')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>{t('adminUI.Provider')}</Label>
                <Select
                  value={cloudStorageForm.provider}
                  onValueChange={(value) => setCloudStorageForm({ ...cloudStorageForm, provider: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aws_s3">AWS S3</SelectItem>
                    <SelectItem value="digitalocean_spaces">DigitalOcean Spaces</SelectItem>
                    <SelectItem value="backblaze_b2">Backblaze B2</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('adminUI.AccessKey')}</Label>
                <Input
                  value={cloudStorageForm.accessKey}
                  onChange={(e) => setCloudStorageForm({ ...cloudStorageForm, accessKey: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t('adminUI.SecretKey')}</Label>
                <Input
                  type="password"
                  value={cloudStorageForm.secretKey}
                  onChange={(e) => setCloudStorageForm({ ...cloudStorageForm, secretKey: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t('adminUI.BucketName')}</Label>
                <Input
                  value={cloudStorageForm.bucketName}
                  onChange={(e) => setCloudStorageForm({ ...cloudStorageForm, bucketName: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t('adminUI.Region')}</Label>
                <Input
                  placeholder="e.g., us-east-1"
                  value={cloudStorageForm.region}
                  onChange={(e) => setCloudStorageForm({ ...cloudStorageForm, region: e.target.value })}
                />
              </div>
              <div>
                <Label>Custom Endpoint (optional)</Label>
                <Input
                  placeholder="For DigitalOcean Spaces or Backblaze"
                  value={cloudStorageForm.customEndpoint}
                  onChange={(e) => setCloudStorageForm({ ...cloudStorageForm, customEndpoint: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCloudStorageDialog(false)}>
                Cancel
              </Button>
              <Button onClick={saveCloudStorageConfig} disabled={savingCloudStorage}>
                {savingCloudStorage ? 'Saving...' : 'Save Configuration'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminUsers;
